import React from 'react';
import { Switch } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faGripVertical, faNoteSticky } from '@fortawesome/free-solid-svg-icons';
import './ProductRow.css';
import type { TableProduct } from '../../types';
import { useI18n } from '../../i18n/I18nContext';
import NumberInput from '../NumberInput/NumberInput';
import { formatEuro } from '../../utils/currency';

// Props for the ProductRow component.
export interface ProductRowProps {
  item: TableProduct;
  listId?: string;
  readOnly?: boolean;
  showPrices?: boolean;
  noteExpanded?: boolean;
  onToggleHandling: () => void;
  onToggleDelivered: () => void;
  onTogglePaid: () => void;
  onEditQty: (value: number) => void;
  onEditName: (value: string) => void;
  onEditNote: (value: string) => void;
  onOpenNote: () => void;
  onCloseNote: () => void;
  onRemove: () => void;
  // Optional flag enabling HTML5 drag‑and‑drop for this row.
  draggable?: boolean;
  // Handler called when dragging starts.
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  // Handler called repeatedly as the dragged item moves over other rows.
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  // Handler called when the dragged item is dropped over this row.
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  // Whether this row is currently a drop target. Used to render a drop indicator.
  isDragOver?: boolean;
}

const ProductRow: React.FC<ProductRowProps> = ({
  item,
  listId = 'product-options',
  readOnly = false,
  showPrices = true,
  noteExpanded = false,
  onToggleHandling,
  onToggleDelivered,
  onTogglePaid,
  onEditQty,
  onEditName,
  onEditNote,
  onOpenNote,
  onCloseNote,
  onRemove,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  isDragOver = false,
}) => {
  const { t } = useI18n();

  return (
    <div
      className={`product-row${item.handling ? ' handling' : ''}${item.delivered ? ' delivered' : ''}${isDragOver ? ' drag-over' : ''}${noteExpanded ? ' product-row--note-expanded' : ''}`}
      draggable={false}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <span
        className="drag-handle"
        draggable={draggable && !readOnly}
        onDragStart={readOnly ? undefined : onDragStart}
        title={t('table.dragToReorder')}
      >
        <FontAwesomeIcon icon={faGripVertical} />
      </span>
      <input
        type="checkbox"
        checked={!!item.handling}
        disabled={readOnly}
        onChange={() => onToggleHandling()}
        title={t('table.handling')}
      />
      <input
        type="checkbox"
        checked={!!item.delivered}
        disabled={readOnly}
        onChange={() => onToggleDelivered()}
        title={t('table.delivered')}
      />
      <div className="paid-toggle-container">
        <Switch
          checked={!!item.paid}
          disabled={readOnly}
          onChange={() => onTogglePaid()}
          size="small"
        />
        {item.paid && <span className="paid-label">{t('table.paid')}</span>}
      </div>
      <NumberInput
        value={item.qty}
        onChange={onEditQty}
        min={1}
        className="number-input--quantity"
        disabled={readOnly}
      />
      <div className="product-name-cell">
        <input
          type="text"
          list={listId}
          value={item.name}
          disabled={readOnly}
          translate="no"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEditName(e.target.value)}
        />
        {showPrices && typeof item.unitPrice === 'number' ? (
          <span className="product-price-badge">{formatEuro(item.unitPrice)}</span>
        ) : null}
      </div>
      {noteExpanded ? (
        <input
          type="text"
          value={item.note ?? ''}
          placeholder={t('table.productNote')}
          disabled={readOnly}
          translate="no"
          autoFocus
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEditNote(e.target.value)}
          onBlur={onCloseNote}
        />
      ) : (
        <button
          type="button"
          className={`product-note-toggle${item.note?.trim() ? ' product-note-toggle--filled' : ''}`}
          disabled={readOnly}
          onClick={onOpenNote}
          aria-label={t('table.addProductNote')}
          title={item.note?.trim() ? `${t('table.productNote')}: ${item.note}` : t('table.addProductNote')}
        >
          <FontAwesomeIcon icon={faNoteSticky} />
        </button>
      )}
      <span
        className={`delete-icon${readOnly ? ' delete-icon--disabled' : ''}`}
        onClick={readOnly ? undefined : onRemove}
      >
        <FontAwesomeIcon icon={faTrash} />
      </span>
    </div>
  );
};

export default ProductRow;
